import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'isomorphic-dompurify';
import { motion } from 'framer-motion';
import formatDate from '../../../lib/dateFormat';
import { cn } from '../../../lib/utils';
import MessageFactory from '../MessageFactory';
import ThreeDotLoader from '../../Loader/ThreeDotLoader';
import useChatStore from '../../../store';
import getTextColor from '../../../hooks/getTextColorContrast';
import { useThemeQuery } from '../../../reactQuery/get';
import { MessageData } from '../../../types';
import MessageContent from '../MessageContent';
import MessageTimestamp from '../MessageTimestamp';

const DEFAULT_PRIMARY_COLOR = '#FF6F98';
const DEFAULT_SECONDARY_COLOR = '#FFE8ED';

function TextMessage({
  text,
  data,
  created_at,
  seen,
  received,
  role,
  loading,
  hasError,
  isLastMessage,
  hasSpeech,
}: MessageData) {
  const { setIsLastMessageRerendered } = useChatStore((state) => state);
  const [displayResponse, setDisplayResponse] = useState<string>('');
  // State to track which messages have had their images displayed
  const [shownImages, setShownImages] = useState<Set<string>>(new Set());
  const [isLastMessageDisplayed, setIsLastMessageDisplayed] = useState(false);

  const isUser = role === 'user';
  const allowedAttributes = { ALLOWED_ATTR: ['href', 'target'] };
  const { organization_id: organizationId, project_id: projectId, chat_id: chatId } = useParams();

  const formattedText = text?.replace(/\n/g, '<br>');
  const sanitizedFormattedText = DOMPurify.sanitize(formattedText || '');
  const sanitizedDisplayResponse = DOMPurify.sanitize(displayResponse || '', allowedAttributes);
  const isSilent = hasSpeech !== undefined && !hasSpeech;

  const projectThemeId = organizationId ? projectId : chatId;
  const { data: themeData } = useThemeQuery(projectThemeId);

  const backgroundColor = useMemo(
    () =>
      isUser
        ? themeData?.['chat-primary'] || DEFAULT_PRIMARY_COLOR
        : themeData?.['chat-secondary'] || DEFAULT_SECONDARY_COLOR,
    [isUser, themeData],
  );

  const textColor = useMemo(() => getTextColor(backgroundColor || '#FFFFFF'), [backgroundColor]);
  // Update shownImages state when data changes, displaying images immediately if they exist
  useEffect(() => {
    // Store file_id of images that have been shown
    if (data && data.length > 0) {
      const fileIds = data
        .map((item) => item.file_id)
        .filter((id): id is string => id !== undefined);
      setShownImages(new Set(fileIds));
    }
  }, [data]);

  // Display the message text immediately with growing bubble animation
  useEffect(() => {
    if (!formattedText?.length || !isLastMessage) {
      return;
    }

    // Set the full text immediately
    setDisplayResponse(formattedText);
    setIsLastMessageDisplayed(true);
    setIsLastMessageRerendered(String(formattedText.length));
  }, [formattedText, isLastMessage, setIsLastMessageRerendered]);

  // Function to render messages (both user and non-user) conditionally
  const renderMessages = () => {
    return data?.map((item, index) => {
      // 1. If image is displayed from the backend  - item.file_id
      // 2. If image is displayed from the frontend - item.url (Uploaded by user and displayed for the first time)
      const fileId = item.file_id || item.url;
      // Determine if the message should be rendered:
      // 1. If the current user is the sender (isUser is true).
      // 2. If there's a fileId and the file has been shown (fileId exists and is present in shownImages).
      const shouldRender = isUser || (fileId && shownImages.has(fileId));
      // If the message should be rendered, return the message element
      if (shouldRender) {
        return (
          <div className="min-h-[22px]" key={fileId || `message-${index}`}>
            {/* Render the message using MessageFactory, passing the item and isUser flag */}
            {MessageFactory({ ...item, isUser, isSilent })}
          </div>
        );
      }
      // If the message shouldn't be rendered, return null
      return null;
    });
  };

  return (
    <div
      className={cn(
        'flex w-full self-end gap-3 md700:gap-1.5 max-w-[85%] min450:max-w-full transition-all',
        isUser ? 'self-end' : 'self-start',
      )}
    >
      <div className={cn('flex flex-col w-full', isUser && 'items-end w-full')}>
        <motion.div
          style={{
            backgroundColor,
            color: textColor,
          }}
          className={cn(
            'w-fit px-3 py-2.5 font-medium md700:ml-0 text-[15px] shadow-md max-w-[85%] min450:max-w-[95%] relative md600:ml-0 rounded-t-xl',
            hasError && '!bg-red-400 border !text-white',
            !!data?.[0]?.url && !!text && 'flex flex-col gap-4',
            isSilent && '!bg-gray-300',
            isUser ? 'rounded-l-xl' : 'rounded-br-xl',
          )}
          initial={{ opacity: 0.5, scale: 0.95, filter: 'blur(3px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* Show loading animation if the message is still being sent */}
          {loading && (
            <div className="px-4 h-[22px] flex items-center">
              <ThreeDotLoader />
            </div>
          )}

          <MessageContent
            isUser={isUser}
            isLastMessage={isLastMessage}
            loading={loading}
            sanitizedFormattedText={sanitizedFormattedText}
            sanitizedDisplayResponse={sanitizedDisplayResponse}
          />

          {/* Conditional rendering for messages based on user and message state */}
          {(isUser || (!isUser && (isLastMessageDisplayed || !isLastMessage))) && (
            <div>{renderMessages()}</div>
          )}
        </motion.div>

        <MessageTimestamp
          date={formatDate(created_at, 'DD.MMM')}
          time={formatDate(created_at, 'hh:mm a')}
          {...{ isUser, seen, received, isSilent }}
        />
      </div>
    </div>
  );
}

export default TextMessage;
